<template>
    <form class="form-container" @submit.prevent="submitForm">
        <!-- Basic Information Section -->
        <section class="form-section">
            <div class="section-header">
                <div class="section-icon">
                    <font-awesome-icon :icon="['fas', 'building']" class="text-lg" />
                </div>
                <h2 class="section-title">Basic Information</h2>
            </div>

            <div class="logo-upload-card">
                <div v-if="logoPreview" class="logo-preview-wrapper">
                    <div @click.stop="cancelUploadedPhoto" class="logo-remove-btn">
                        <icon-close class="h-3.5 w-3.5"></icon-close>
                    </div>
                    <img class="logo-preview-img" :src="logoPreview" alt="Logo preview">
                </div>
                <div v-else class="logo-placeholder">
                    <div class="logo-placeholder-icon">
                        <font-awesome-icon :icon="['fas', 'image']" class="text-4xl text-slate-400" />
                    </div>
                    <p class="logo-placeholder-text">Partner logo will appear here</p>
                </div>
                <input ref="logoPicture" class="hidden" id="profile-picture" @change="handleUploadedPhoto" type="file"
                    accept="image/*">
                <button type="button" @click.stop="onPickFile('logoPicture')"
                    :class="['logo-upload-btn', logoPicture.isValid ? '' : 'logo-upload-btn-error']">
                    <icon-plus class="h-4 w-4"></icon-plus>
                    <span>Upload Logo</span>
                    <span class="logo-upload-hint">JPG, JPEG, PNG · max 2MB</span>
                </button>
                <transition name="fade" mode="out-in">
                    <p v-if="!logoPicture.isValid" class="form-error-msg">{{ logoPicture.msg }}</p>
                </transition>
            </div>

            <div class="form-grid">
                <div class="form-group" :class="{ 'form-group-error': !name.isValid }">
                    <label for="name" class="form-label">Partner Name <span class="required">*</span></label>
                    <input type="text" id="name" v-model.trim="name.val" placeholder="Enter partner name"
                        class="form-input" @blur="clearValidity('name')" />
                    <transition name="fade" mode="out-in">
                        <p v-if="!name.isValid" class="form-error-msg">{{ name.msg }}</p>
                    </transition>
                </div>
                <div class="form-group" :class="{ 'form-group-error': !industry.isValid }">
                    <label for="industry" class="form-label">Industry <span class="required">*</span></label>
                    <select id="industry" v-model="industry.val" class="form-input form-select"
                        @change="onIndustryChange">
                        <option value="">Select industry</option>
                        <option v-for="ind in industryOptions" :key="ind.id" :value="ind.id">{{ ind.name }}</option>
                        <option value="other">Other (enter manually)</option>
                    </select>
                    <transition name="fade" mode="out-in">
                        <p v-if="!industry.isValid" class="form-error-msg">{{ industry.msg }}</p>
                    </transition>
                </div>
                <div v-if="industry.val === 'other'" class="form-group" :class="{ 'form-group-error': !industryOther.isValid }">
                    <label for="industry-other" class="form-label">Enter Industry Name <span class="required">*</span></label>
                    <input type="text" id="industry-other" v-model.trim="industryOther.val"
                        placeholder="Enter new industry name" class="form-input"
                        @blur="validateIndustryOther" />
                    <transition name="fade" mode="out-in">
                        <p v-if="!industryOther.isValid" class="form-error-msg">{{ industryOther.msg }}</p>
                    </transition>
                </div>
                <div class="form-group">
                    <label for="type-of-collaboration" class="form-label">Type of Collaboration</label>
                    <select id="type-of-collaboration" v-model="selectedTypeOfCollaboration"
                        class="form-input form-select">
                        <option value="">Select type</option>
                        <option v-for="opt in typeOfCollaborationOptions" :key="opt" :value="opt">{{ opt }}</option>
                    </select>
                </div>
                <div class="form-group form-group-multiselect">
                    <label class="form-label">Classifications</label>
                    <multiselect v-model="selectedClassifications" :options="classificationOptions"
                        :multiple="true" :close-on-select="false" placeholder="Select classifications"
                        class="form-multiselect">
                    </multiselect>
                </div>
                <div class="form-group" :class="{ 'form-group-error': !status.isValid }">
                    <label for="status" class="form-label">Status <span class="required">*</span></label>
                    <select id="status" v-model.trim="status.val" @change="clearValidity('status')"
                        class="form-input form-select">
                        <option value="">Select status</option>
                        <option value="Exploring Phase">Exploring Phase</option>
                        <option value="Drafting">Drafting</option>
                        <option value="Management Approval">Management Approval</option>
                        <option value="Second party review">Second party review</option>
                        <option value="Legal approval">Legal approval</option>
                        <option value="Security clearance">Security clearance</option>
                        <option value="Signature phase">Signature phase</option>
                    </select>
                    <transition name="fade" mode="out-in">
                        <p v-if="!status.isValid" class="form-error-msg">{{ status.msg }}</p>
                    </transition>
                </div>
                <div class="form-group" :class="{ 'form-group-error': !note.isValid }">
                    <label for="note" class="form-label">Remarks</label>
                    <textarea id="note" v-model.trim="note.val" @blur="clearValidity('note')"
                        placeholder="Add your remarks..." rows="3"
                        :class="['form-input form-textarea', note.isValid ? '' : 'form-input-error']"></textarea>
                    <transition name="fade" mode="out-in">
                        <p v-if="!note.isValid" class="form-error-msg">{{ note.msg }}</p>
                    </transition>
                </div>
            </div>

            <div class="form-group mt-4">
                <label class="form-label">Is this company part of a group/mother company?</label>
                <select v-model="isPartOfGroupCompany" class="form-input form-select">
                    <option value="">No</option>
                    <option value="yes">Yes</option>
                </select>
            </div>
            <div v-if="isPartOfGroupCompany === 'yes'" class="form-group mt-4">
                <label class="form-label">Select Group/Mother Company</label>
                <multiselect v-model="selectedParentPartner" :options="partnersForSelect" :multiple="false"
                    :close-on-select="true" placeholder="Select partner" label="name" track-by="id">
                    <template #singleLabel="props">
                        <span class="option__desc">
                            <span class="option__title">{{ props.option.name }}</span>
                            <span class="option__small">({{ props.option.is_pipeline ? 'Pipeline' : 'Current' }})</span>
                        </span>
                    </template>
                    <template #option="props">
                        <span class="option__desc">
                            <span class="option__title">{{ props.option.name }}</span>
                            <span class="option__small">({{ props.option.is_pipeline ? 'Pipeline' : 'Current' }})</span>
                        </span>
                    </template>
                </multiselect>
            </div>

            <div class="form-group entity-group" :class="{ 'form-group-error': !entity.isValid }">
                <label class="form-label">Entity <span class="required">*</span></label>
                <div class="entity-checkbox-grid">
                    <label v-for="(entity, index) in entityList" :key="index"
                        :class="['entity-checkbox-item', selectedEntities.includes(entity) ? 'entity-checkbox-item-selected' : '']">
                        <input type="checkbox" v-model="selectedEntities" :value="entity"
                            @blur="clearValidity('entity')" class="entity-checkbox-input">
                        <span class="entity-checkbox-label">{{ getEntityName(entity) }}</span>
                    </label>
                </div>
                <transition name="fade" mode="out-in">
                    <p v-if="!entity.isValid" class="form-error-msg">{{ entity.msg }}</p>
                </transition>
            </div>
        </section>

        <!-- Management Approval Section -->
        <section v-if="status.val == 'Management Approval'" class="form-section">
            <div class="section-header">
                <div class="section-icon section-icon-approval">
                    <font-awesome-icon :icon="['fas', 'stamp']" class="text-lg" />
                </div>
                <h2 class="section-title">Management Approval</h2>
            </div>

            <div class="approval-grid">
                <div class="approval-item">
                    <label class="form-label">Board of Trustees (BoT) approval?</label>
                    <div class="approval-row">
                        <label class="approval-radio">
                            <input type="radio" v-model="botApproval.val" :value="true" class="approval-radio-input" />
                            <span class="approval-radio-label">Yes</span>
                        </label>
                        <label class="approval-radio">
                            <input type="radio" v-model="botApproval.val" :value="false" class="approval-radio-input" />
                            <span class="approval-radio-label">No</span>
                        </label>
                        <div class="approval-file-wrapper">
                            <input :id="`botApproval-file-0`" :key="`botApproval-file-0`" type="file"
                                class="approval-file-input"
                                @change="handleManagementApprovalFile($event, 'botApproval')"
                                accept="image/*,application/pdf,.doc,.docx,.ppt,.pptx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation" />
                        </div>
                    </div>
                </div>

                <div class="approval-item">
                    <label class="form-label">Managing Director's (MD) Approval?</label>
                    <div class="approval-row">
                        <label class="approval-radio">
                            <input type="radio" v-model="mdApproval.val" :value="true" class="approval-radio-input" />
                            <span class="approval-radio-label">Yes</span>
                        </label>
                        <label class="approval-radio">
                            <input type="radio" v-model="mdApproval.val" :value="false" class="approval-radio-input" />
                            <span class="approval-radio-label">No</span>
                        </label>
                        <div class="approval-file-wrapper">
                            <input :id="`mdApproval-file-0`" :key="`mdApproval-file-0`" type="file"
                                class="approval-file-input" @change="handleManagementApprovalFile($event, 'mdApproval')"
                                accept="image/*,application/pdf,.doc,.docx,.ppt,.pptx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation" />
                        </div>
                    </div>
                </div>
                <div class="approval-item">
                    <label class="form-label">Line Manager’s Approval?</label>
                    <div class="approval-row">
                        <label class="approval-radio">
                            <input type="radio" v-model="lineManagerApproval.val" :value="true"
                                class="approval-radio-input" />
                            <span class="approval-radio-label">Yes</span>
                        </label>
                        <label class="approval-radio">
                            <input type="radio" v-model="lineManagerApproval.val" :value="false"
                                class="approval-radio-input" />
                            <span class="approval-radio-label">No</span>
                        </label>
                        <div class="approval-file-wrapper">
                            <input :id="`lineManagerApproval-file-0`" :key="`lineManagerApproval-file-0`" type="file"
                                class="approval-file-input"
                                @change="handleManagementApprovalFile($event, 'lineManagerApproval')"
                                accept="image/*,application/pdf,.doc,.docx,.ppt,.pptx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation" />
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- Contact Person Section -->
        <section class="form-section">
            <div class="section-header">
                <div class="section-icon">
                    <font-awesome-icon :icon="['fas', 'users']" class="text-lg" />
                </div>
                <h2 class="section-title">Contact Person</h2>
            </div>
            <div v-for="(item, index) in inputContactPerson" :key="index" class="contact-person-row">
                <div class="contact-person-badge">{{ index + 1 }}</div>
                <div class="contact-person-fields">
                    <div class="form-group">
                        <label :for="`cp-name-${index + 1}`" class="form-label">Name <span
                                class="required">*</span></label>
                        <input type="text" :id="`cp-name-${index + 1}`" v-model.trim="item.name" placeholder="Full name"
                            class="form-input" />
                    </div>
                    <div class="form-group">
                        <label :for="`cp-phone-${index + 1}`" class="form-label">Phone <span
                                class="required">*</span></label>
                        <input type="text" :id="`cp-phone-${index + 1}`" v-model.trim="item.phone"
                            placeholder="Phone number" class="form-input" @keypress="allowNumbersOnly" />
                    </div>
                    <div class="form-group">
                        <label :for="`cp-email-${index + 1}`" class="form-label">Email <span
                                class="required">*</span></label>
                        <input type="text" :id="`cp-email-${index + 1}`" v-model.trim="item.email"
                            placeholder="email@example.com" class="form-input" />
                    </div>
                    <div class="form-group">
                        <label :for="`cp-designation-${index + 1}`" class="form-label">Designation <span
                                class="required">*</span></label>
                        <input type="text" :id="`cp-designation-${index + 1}`" v-model.trim="item.designation"
                            placeholder="Job title" class="form-input" />
                    </div>
                </div>
                <div class="contact-person-actions">
                    <button v-if="showAddMoreContactPersonBtn(index)" type="button" @click.stop="addMoreContactPerson"
                        class="action-btn action-btn-add">
                        <icon-plus class="h-4 w-4"></icon-plus>
                    </button>
                    <button v-if="inputContactPerson.length > 1" type="button" @click.stop="removeContactPerson(index)"
                        class="action-btn action-btn-remove">
                        <icon-minus class="h-4 w-4"></icon-minus>
                    </button>
                </div>
            </div>
            <transition name="form-fade" mode="out-in">
                <p v-if="contactPersonErrorMsg" class="form-error-msg form-error-msg-center">{{ contactPersonErrorMsg }}
                </p>
            </transition>
        </section>

        <!-- Upload Documents Section -->
        <section class="form-section">
            <div class="section-header">
                <div class="section-icon">
                    <font-awesome-icon :icon="['fas', 'paperclip']" class="text-lg" />
                </div>
                <h2 class="section-title">Upload Documents</h2>
            </div>

            <div v-for="(item, index) in inputAttachments" :key="index" class="document-row">
                <div class="document-badge">{{ index + 1 }}</div>
                <div class="document-fields">
                    <div class="form-group">
                        <label :for="`cp-at-name-${index + 1}`" class="form-label">Document Name</label>
                        <input type="text" :id="`cp-at-name-${index + 1}`" v-model.trim="item.file_name"
                            placeholder="e.g. Partnership Agreement" class="form-input" :disabled="!item.is_new"
                            :class="{ 'form-input-disabled': !item.is_new }" />
                    </div>
                    <div class="form-group">
                        <div v-if="!item.is_new" class="document-existing">
                            <icon-paper-clip class="h-4 w-4 text-slate-500 flex-shrink-0"></icon-paper-clip>
                            <a :href="getSrcFile(item.path)" target="_blank" class="document-link">{{
                                item.original_filename }}</a>
                            <button type="button" @click.stop="removeAttachedFile(index)" class="document-remove-btn">
                                <icon-close class="h-4 w-4"></icon-close>
                            </button>
                        </div>
                        <div v-else>
                            <label :for="`document-file-${index}`" class="form-label">File</label>
                            <div class="file-input-wrapper">
                                <input :id="`document-file-${index}`" :key="`document-file-${index}`" type="file"
                                    class="file-input" @change="handleFileChange"
                                    accept="image/*,application/pdf,.doc,.docx,.ppt,.pptx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation" />
                                <span class="file-input-hint">JPG, PNG, PDF, DOC, PPT · max 16MB</span>
                            </div>
                            <transition name="fade" mode="out-in">
                                <p v-if="!item.document.isValid" class="form-error-msg">{{ item.document.msg }}</p>
                            </transition>
                        </div>
                    </div>
                </div>
                <div class="document-actions">
                    <button v-if="showAddMoreAttachmentBtn(index)" type="button" @click.stop="addMoreAttachment"
                        class="action-btn action-btn-add">
                        <icon-plus class="h-4 w-4"></icon-plus>
                    </button>
                    <button v-if="inputAttachments.length > 1" type="button" @click.stop="removeAttachment(index)"
                        class="action-btn action-btn-remove">
                        <icon-minus class="h-4 w-4"></icon-minus>
                    </button>
                </div>
            </div>
            <transition name="form-fade" mode="out-in">
                <p v-if="attachmentErrorMsg" class="form-error-msg form-error-msg-center">{{ attachmentErrorMsg }}</p>
            </transition>
        </section>

        <!-- Submit Section -->
        <div class="form-footer">
            <div v-if="isLoading" class="submit-loading">
                <base-spinner-with-text class-list="h-6 w-6 text-ternary" :text="getLoadingBtnText"
                    text-class-list="text-ternary font-medium"></base-spinner-with-text>
            </div>
            <button v-else type="submit" class="submit-btn">
                {{ getBtnText }}
            </button>
        </div>
    </form>
    <div class="h-full flex justify-center items-center" v-if="isLoading">
        <base-page-spinner type="spin" class-list="h-20"></base-page-spinner>
    </div>
</template>

<script>
import gql from 'graphql-tag';
import IconClose from "../../components/icons/IconClose.vue";
import IconPlus from "../../components/icons/IconPlus.vue";
import IconMinus from "../../components/icons/IconMinus.vue";
import IconPaperClip from "../../components/icons/IconPaperClip.vue";
import Multiselect from "vue-multiselect";
import "vue-multiselect/dist/vue-multiselect.css";
export default {
    name: "pipeline-partners-form",
    emits: ["created", "updated", "close"],
    props: ["mode", "selectedItem", "entities"],
    components: {
        IconClose,
        IconPlus,
        IconMinus,
        IconPaperClip,
        Multiselect,
    },
    data() {
        return {
            entityList: this.entities.map(item => item.entity),
            logoPicture: {
                val: null,
                isValid: true,
                msg: "",
            },
            logoPreview: null,
            name: {
                val: "",
                isValid: true,
                msg: "",
            },
            industry: {
                val: "",
                isValid: true,
                msg: "",
            },
            industryOther: {
                val: "",
                isValid: true,
                msg: "",
            },
            industryOptions: [],
            entity: {
                val: "",
                isValid: true,
                msg: "",
            },
            status: {
                val: "",
                isValid: true,
                msg: "",
            },
            note: {
                val: "",
                isValid: true,
                msg: "",
            },
            typeOfCollaborationOptions: ["NDA", "MOU", "Agreement", "Contract"],
            selectedTypeOfCollaboration: "",
            selectedClassifications: [],
            classificationOptions: [
                "Employment-focused",
                "Training-focused",
                "Research-focused",
                "Sponsorship-focused",
                "Project-focused",
                "General partnership",
                "Shelved (to be upgraded or discontinued)",
            ],
            botApproval: {
                val: null,
                isValid: true,
                msg: "",
                attachments: [
                    {
                        document: {
                            val: null,
                            isValid: true,
                            msg: "",
                        },
                    }
                ],
            },
            botApprovalAttachmentIndex: 0,
            mdApproval: {
                val: null,
                isValid: true,
                msg: "",
                attachments: [
                    {
                        document: {
                            val: null,
                            isValid: true,
                            msg: "",
                        },
                    }
                ],
            },
            mdApprovalAttachmentIndex: 0,
            lineManagerApproval: {
                val: null,
                isValid: true,
                msg: "",
                attachments: [
                    {
                        document: {
                            val: null,
                            isValid: true,
                            msg: "",
                        },
                    }
                ],
            },
            lineManagerApprovalAttachmentIndex: 0,
            inputContactPerson: [],
            contactPersonIndex: 0,
            contactPersonErrorMsg: "",
            inputAttachments: [],
            attachmentIndex: 0,
            attachmentErrorMsg: "",
            deletedAttachmentIds: [],
            formIsValid: true,
            isLoading: false,
            selectedEntities: [],
            isPartOfGroupCompany: "",
            selectedParentPartner: null,
            partnersForSelect: [],
        };
    },
    apollo: {
        getAllPartnersForSelect: {
            query: gql`
                query GetAllPartnersForSelect($exclude_partner_id: Int) {
                    getAllPartnersForSelect(exclude_partner_id: $exclude_partner_id) {
                        error
                        message
                        data {
                            id
                            name
                            token
                            is_pipeline
                        }
                    }
                }
            `,
            variables() {
                return {
                    exclude_partner_id: this.mode === 'edit' && this.selectedItem && this.selectedItem.id
                        ? parseInt(this.selectedItem.id, 10)
                        : null,
                };
            },
            skip() {
                return false;
            },
            result({ data }) {
                if (data && data.getAllPartnersForSelect && !data.getAllPartnersForSelect.error && data.getAllPartnersForSelect.data) {
                    this.partnersForSelect = data.getAllPartnersForSelect.data;
                }
            },
        },
    },
    watch: {
        partnersForSelect(newVal) {
            if (this.mode === 'edit' && this.selectedItem && this.selectedItem.parent_partner_id && newVal.length > 0 && !this.selectedParentPartner) {
                const found = newVal.find(p => p.id === this.selectedItem.parent_partner_id);
                if (found) {
                    this.selectedParentPartner = found;
                }
            }
        },
    },
    created() {
        if (this.mode == 'edit') {
            this.setFormData();
        } else {
            this.initializeVariables();
        }
    },
    mounted() {
        this.fetchPartnersForSelect();
        this.fetchIndustryOptions();
    },
    computed: {
        getBtnText() {
            if (this.mode == 'create') {
                return "Create";
            } else {
                return "Update";
            }
        },
        getLoadingBtnText() {
            if (this.mode == 'create') {
                return "Creating...";
            } else {
                return "Updating...";
            }
        },
    },
    methods: {
        isValidFileType(file) {
            const validImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
            const validPdfType = 'application/pdf';
            const validDocTypes = [
                'application/msword',
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
            ];
            const validPptTypes = [
                'application/vnd.ms-powerpoint',
                'application/vnd.openxmlformats-officedocument.presentationml.presentation',
                'application/vnd.ms-powerpoint.presentation.macroEnabled.12'
            ];

            const fileType = file.type.toLowerCase();

            return validImageTypes.includes(fileType) ||
                fileType === validPdfType ||
                validDocTypes.includes(fileType) ||
                validPptTypes.includes(fileType) ||
                file.name.toLowerCase().endsWith('.doc') ||
                file.name.toLowerCase().endsWith('.docx') ||
                file.name.toLowerCase().endsWith('.ppt') ||
                file.name.toLowerCase().endsWith('.pptx');
        },
        getEntityName(entity) {
            return entity.replace(/ATHS/g, "ATS");
        },
        async fetchPartnersForSelect() {
            try {
                const excludeId = this.mode === 'edit' && this.selectedItem && this.selectedItem.id
                    ? parseInt(this.selectedItem.id, 10)
                    : null;
                const { data } = await this.$apollo.query({
                    query: gql`
                        query GetAllPartnersForSelect($exclude_partner_id: Int) {
                            getAllPartnersForSelect(exclude_partner_id: $exclude_partner_id) {
                                error
                                message
                                data {
                                    id
                                    name
                                    token
                                    is_pipeline
                                }
                            }
                        }
                    `,
                    variables: { exclude_partner_id: excludeId },
                    fetchPolicy: 'network-only',
                });
                if (data && data.getAllPartnersForSelect && !data.getAllPartnersForSelect.error && data.getAllPartnersForSelect.data) {
                    this.partnersForSelect = data.getAllPartnersForSelect.data;
                }
            } catch (err) {
                console.error('Failed to fetch partners for select:', err);
            }
        },
        getSrcFile(url) {
            return process.env.VUE_APP_PUBLIC_URL + url;
        },
        async fetchIndustryOptions() {
            try {
                var result = await this.$apollo.query({
                    query: gql`
                        query AllIndustries {
                            allIndustries {
                                id
                                name
                            }
                        }
                    `,
                    fetchPolicy: 'network-only',
                });
                if (result && result.data && result.data.allIndustries) {
                    this.industryOptions = result.data.allIndustries;
                }
            } catch (err) {
                console.error('Failed to fetch industries:', err);
            }
        },
        onIndustryChange() {
            this.clearValidity('industry');
            if (this.industry.val !== 'other') {
                this.industryOther.val = '';
                this.industryOther.isValid = true;
                this.industryOther.msg = '';
            }
        },
        async validateIndustryOther() {
            var name = this.industryOther.val;
            if (!name) {
                return;
            }
            try {
                var result = await this.$apollo.query({
                    query: gql`
                        query ValidateIndustryName($name: String!) {
                            validateIndustryName(name: $name) {
                                valid
                                message
                                suggestion {
                                    id
                                    name
                                }
                            }
                        }
                    `,
                    variables: { name: name },
                    fetchPolicy: 'network-only',
                });
                var payload = result && result.data ? result.data.validateIndustryName : null;
                if (payload && !payload.valid) {
                    this.industryOther.isValid = false;
                    this.industryOther.msg = payload.message || 'This name is too similar to an existing industry.';
                } else {
                    this.industryOther.isValid = true;
                    this.industryOther.msg = '';
                }
            } catch (err) {
                console.error('Failed to validate industry name:', err);
            }
        },
        allowNumbersOnly(event) {
            const charCode = event.which || event.keyCode;
            if (charCode > 31 && (charCode < 48 || charCode > 57)) {
                event.preventDefault();
            }
        },
        clearValidity(input) {
            this[input].isValid = true;
            this[input].msg = "";
        },
        validateAttachment() {
            this.attachmentErrorMsg = "";
            if (this.mode == 'create') {
                const noFileUploaded = this.inputAttachments.some((obj) => obj.document.val === null);
                if (this.inputAttachments[this.attachmentIndex].file_name && !noFileUploaded) {
                    return true;
                } else {
                    return false;
                }
            } else {
                const newAttachments = this.inputAttachments.filter(attachment => attachment.is_new);
                newAttachments.forEach(attachment => {
                    if (attachment.document.val === null || attachment.file_name == '') {
                        return false;
                    }
                });
                if (this.inputAttachments[this.attachmentIndex].is_new) {
                    if (this.inputAttachments[this.attachmentIndex].document.val == null || this.inputAttachments[this.attachmentIndex].file_name === '') {
                        return false;
                    }
                } else {
                    if (this.inputAttachments[this.attachmentIndex].file_name == '') {
                        return false;
                    }
                }
                return true;
            }
        },
        validateAttachmentInEditMode() {
            this.attachmentErrorMsg = "";
            const newAttachments = this.inputAttachments.filter(attachment => attachment.is_new);
            newAttachments.forEach(attachment => {
                if (attachment.document.val === null && attachment.file_name != '') {
                    attachment.document.isValid = false;
                    attachment.document.msg = "Document is required";
                    this.formIsValid = false;
                } else {
                    attachment.document.isValid = true;
                    attachment.document.msg = "";
                }
            });
            this.inputAttachments.forEach(attachment => {

                if (attachment.is_new) {
                    if (attachment.document.val != null && attachment.file_name === '') {
                        this.attachmentErrorMsg = "Please fill all the details.";
                        this.formIsValid = false;
                    }
                } else {
                    if (attachment.file_name === '') {
                        this.attachmentErrorMsg = "Please fill all the details.";
                        this.formIsValid = false;
                    }
                }


            });
        },
        addMoreAttachment() {
            const isValid = this.validateAttachment();
            if (isValid) {
                const obj = {
                    is_new: true,
                    id: null,
                    original_filename: "",
                    path: "",
                    file_name: "",
                    document: {
                        val: null,
                        isValid: true,
                        msg: "",
                    }
                }
                this.inputAttachments.push(obj);
                this.attachmentIndex = this.attachmentIndex + 1;
            } else {
                this.attachmentErrorMsg = "You must upload a file and document name must not be empty.";
            }
        },
        removeAttachment(index) {
            if (this.mode == 'edit') {
                this.deletedAttachmentIds.push(parseInt(this.inputAttachments[index].id));
            }
            this.inputAttachments.splice(index, 1);
            this.attachmentIndex = this.attachmentIndex - 1;
        },
        removeAttachedFile(index) {
            this.deletedAttachmentIds.push(parseInt(this.inputAttachments[index].id));
            this.inputAttachments[index].id = null;
            this.inputAttachments[index].file_name = "";
            this.inputAttachments[index].is_new = true;
        },
        showAddMoreAttachmentBtn(index) {
            if (this.inputAttachments.length <= 1) {
                return true;
            } else {
                if ((this.inputAttachments.length - 1) == index) {
                    return true;
                } else {
                    return false;
                }
            }
        },
        validateContactPerson() {
            this.contactPersonErrorMsg = "";
            if (this.inputContactPerson[this.contactPersonIndex].name && this.inputContactPerson[this.contactPersonIndex].phone && this.inputContactPerson[this.contactPersonIndex].email && this.inputContactPerson[this.contactPersonIndex].designation) {
                return true;
            } else {
                return false;
            }
        },
        addMoreContactPerson() {
            const isValid = this.validateContactPerson();
            if (isValid) {
                const obj = {
                    name: "",
                    phone: "",
                    email: "",
                    designation: "",
                }
                this.inputContactPerson.push(obj);
                this.contactPersonIndex = this.contactPersonIndex + 1;
            } else {
                this.contactPersonErrorMsg = "You must fill all the details.";
            }
        },
        removeContactPerson(index) {
            this.inputContactPerson.splice(index, 1);
            this.contactPersonIndex = this.contactPersonIndex - 1;
        },
        showAddMoreContactPersonBtn(index) {
            if (this.inputContactPerson.length <= 1) {
                return true;
            } else {
                if ((this.inputContactPerson.length - 1) == index) {
                    return true;
                } else {
                    return false;
                }
            }
        },
        validateForm() {
            this.formIsValid = true;
            if (this.mode == 'edit') {
                if (this.logoPicture.val === null && this.logoPreview === null) {
                    this.logoPicture.isValid = false;
                    this.logoPicture.msg = "You must upload a logo.";
                    this.formIsValid = false;
                }
            } else {
                if (this.logoPicture.val === null) {
                    this.logoPicture.isValid = false;
                    this.logoPicture.msg = "You must upload a logo.";
                    this.formIsValid = false;
                }
            }
            if (this.name.val === "") {
                this.name.isValid = false;
                this.name.msg = "Name must not be empty.";
                this.formIsValid = false;
            }
            if (this.selectedEntities.length <= 0) {
                this.entity.isValid = false;
                this.entity.msg = "You must select any of the option.";
                this.formIsValid = false;
            } else {
                this.entity.val = this.selectedEntities.join(',');
            }
            if (this.industry.val === "" || this.industry.val === null) {
                this.industry.isValid = false;
                this.industry.msg = "Please select an industry.";
                this.formIsValid = false;
            }
            if (this.industry.val === "other") {
                if (!this.industryOther.val || this.industryOther.val === "") {
                    this.industryOther.isValid = false;
                    this.industryOther.msg = "Please enter the industry name.";
                    this.formIsValid = false;
                } else if (!this.industryOther.isValid) {
                    this.formIsValid = false;
                }
            }
            if (this.status.val === "") {
                this.status.isValid = false;
                this.status.msg = "You must select a status.";
                this.formIsValid = false;
            }
            if (this.inputContactPerson.length > 1) {
                const isValid = this.validateContactPerson();
                if (!isValid) {
                    this.contactPersonErrorMsg = "You must fill all the details.";
                    this.formIsValid = false;
                }
            } else {
                const isValid = this.validateContactPerson();
                if (!isValid) {
                    this.contactPersonErrorMsg = "You must enter at least 1 contact person.";
                    this.formIsValid = false;
                }
            }

            if (this.mode == 'edit') {
                this.validateAttachmentInEditMode();
            }
        },
        async submitForm() {

            this.validateForm();

            if (!this.formIsValid) {
                return;
            }

            let modifiedInputAttachments = this.inputAttachments.filter(item => item.document.val != null).map(item => {
                return {
                    id: parseInt(item.id),
                    file_name: item.file_name,
                    document: item.document.val
                };
            });

            let botApprovalAttachments = this.botApproval.attachments.filter(item => item.document.val != null).map(item => {
                return {
                    document: item.document.val
                };
            });

            let mdApprovalAttachments = this.mdApproval.attachments.filter(item => item.document.val != null).map(item => {
                return {
                    document: item.document.val
                };
            });

            let lineManagerApprovalAttachments = this.lineManagerApproval.attachments.filter(item => item.document.val != null).map(item => {
                return {
                    document: item.document.val
                };
            });

            const parentPartnerId = this.isPartOfGroupCompany === 'yes' && this.selectedParentPartner
                ? parseInt(this.selectedParentPartner.id, 10)
                : null;

            var industryId = this.industry.val === "other" ? 0 : parseInt(this.industry.val, 10);
            var industryOtherVal = this.industry.val === "other" ? this.industryOther.val : null;

            const formData = {
                logo_picture: this.logoPicture.val,
                logo: this.mode == 'create' ? '' : this.selectedItem.logo,
                name: this.name.val,
                entity: this.entity.val,
                industry: industryId,
                industry_other: industryOtherVal,
                type_of_collaboration: this.selectedTypeOfCollaboration || null,
                classifications: this.selectedClassifications && this.selectedClassifications.length > 0
                    ? this.selectedClassifications
                    : null,
                parent_partner_id: parentPartnerId,
                status: this.status.val,
                note: this.note.val,
                input_contact_person: this.inputContactPerson,
                input_attachments: modifiedInputAttachments,
                deleted_attachment_ids: this.deletedAttachmentIds,
                is_pipeline: true,
                is_bot_approval: this.botApproval.val,
                bot_approval_attachments: botApprovalAttachments,
                is_md_approval: this.mdApproval.val,
                md_approval_attachments: mdApprovalAttachments,
                is_line_manager_approval: this.lineManagerApproval.val,
                line_manager_approval_attachments: lineManagerApprovalAttachments,
            };

            const CREATE_MUTATION = gql`
                mutation CreatePipelinePartner($input: CurrentPartnerInput!) {
                    createCurrentPartner(input: $input) {
                        error
                        message
                    }
                }
            `;

            const UPDATE_MUTATION = gql`
                mutation UpdatePipelinePartner($token: String!, $input: CurrentPartnerInput!) {
                    updateCurrentPartner(token: $token, input: $input) {
                        error
                        message
                    }
                }
            `;

            this.isLoading = true;
            let error_msg = null;
            let success_msg = null;

            try {
                await this.$apollo.mutate({
                    mutation: this.mode == 'create' ? CREATE_MUTATION : UPDATE_MUTATION,
                    variables: {
                        token: this.mode == 'edit' ? this.selectedItem.token : null,
                        input: formData
                    },
                    context: {
                        headers: {
                            'content-type': 'multipart/form-data',
                        },
                    },
                }).then((data) => {
                    if (this.mode == 'create') {
                        if (data.data.createCurrentPartner.error) {
                            error_msg = data.data.createCurrentPartner.message;
                        } else {
                            success_msg = data.data.createCurrentPartner.message;
                        }
                    } else {
                        if (data.data.updateCurrentPartner.error) {
                            error_msg = data.data.updateCurrentPartner.message;
                        } else {
                            success_msg = data.data.updateCurrentPartner.message;
                        }
                    }
                }).catch((error) => {
                    error_msg = error;
                })

            } catch (error) {
                error_msg = error.message || "Failed to authenticate, try later.";
            }

            if (error_msg === null) {
                if (this.mode == 'create') {
                    this.$emit('created', success_msg);
                } else {
                    this.$emit('updated', success_msg);
                }
            } else {
                this.$swal.fire({
                    icon: 'error',
                    title: 'Oops...',
                    text: error_msg,
                }).then((result) => {
                    if (result.value || result.dismiss) {
                        this.$emit('close');
                    }
                })
            }

            this.isLoading = false;

        },
        initializeVariables() {
            const obj1 = {
                name: "",
                phone: "",
                email: "",
                designation: "",
            }
            this.inputContactPerson.push(obj1);
            const obj2 = {
                is_new: true,
                id: null,
                original_filename: "",
                path: "",
                file_name: "",
                document: {
                    is_new: true,
                    val: null,
                    isValid: true,
                    msg: "",
                }
            }
            this.inputAttachments.push(obj2);
        },
        setFormData() {
            if (this.selectedItem.logo) {
                this.logoPreview = process.env.VUE_APP_PUBLIC_URL + this.selectedItem.logo;
            }
            this.name.val = this.selectedItem.name;
            this.selectedEntities = this.selectedItem.entity.split(',');
            this.entity.val = this.selectedItem.entity;
            this.industry.val = this.selectedItem.industry ? this.selectedItem.industry : "";
            this.selectedTypeOfCollaboration = this.selectedItem.type_of_collaboration
                ? this.selectedItem.type_of_collaboration.split(",")[0].trim()
                : "";
            if (this.selectedItem.classifications && this.selectedItem.classifications.length > 0) {
                this.selectedClassifications = this.selectedItem.classifications.slice();
            } else {
                this.selectedClassifications = [];
            }
            this.status.val = this.selectedItem.status ? this.selectedItem.status : "";
            this.note.val = this.selectedItem.note ? this.selectedItem.note : "";
            this.isPartOfGroupCompany = this.selectedItem.parent_partner_id ? 'yes' : '';
            if (this.selectedItem.parent_partner_id) {
                const found = this.partnersForSelect.find(
                    p => p.id === this.selectedItem.parent_partner_id
                );
                if (found) {
                    this.selectedParentPartner = found;
                } else if (this.selectedItem.parent_partner && this.selectedItem.parent_partner.id) {
                    const parentObj = {
                        id: this.selectedItem.parent_partner.id,
                        name: this.selectedItem.parent_partner.name,
                        token: this.selectedItem.parent_partner.token,
                        is_pipeline: this.selectedItem.parent_partner.is_pipeline,
                    };
                    this.selectedParentPartner = parentObj;
                    this.partnersForSelect.push(parentObj);
                } else {
                    this.selectedParentPartner = null;
                }
            } else {
                this.selectedParentPartner = null;
            }
            this.botApproval.val = this.selectedItem.is_bot_approval;
            this.mdApproval.val = this.selectedItem.is_md_approval;
            this.lineManagerApproval.val = this.selectedItem.is_line_manager_approval;
            if (this.selectedItem.contact_person.length > 0) {
                this.contactPersonIndex = this.selectedItem.contact_person.length - 1;
                this.selectedItem.contact_person.forEach(cp => {
                    const { name, phone, email, designation } = cp;
                    this.inputContactPerson.push({ name, phone, email, designation });
                });
            } else {
                const obj1 = {
                    name: "",
                    phone: "",
                    email: "",
                    designation: "",
                }
                this.inputContactPerson.push(obj1);
            }
            if (this.selectedItem.attachments.length > 0 && this.selectedItem.attachments.some(att => att.uploaded_from === 'Upload Documents')) {
                this.attachmentIndex = this.selectedItem.attachments.length - 1;
                this.selectedItem.attachments.forEach(cp => {
                    if (cp.uploaded_from === 'Upload Documents') {
                        const obj3 = {
                            is_new: false,
                            id: cp.id,
                            original_filename: cp.original_filename,
                            path: cp.path,
                            file_name: cp.file_name,
                            document: {
                                val: null,
                                isValid: true,
                                msg: "",
                            }
                        }
                        this.inputAttachments.push(obj3);
                    }
                });
            } else {
                const obj2 = {
                    is_new: true,
                    id: null,
                    original_filename: "",
                    path: "",
                    file_name: "",
                    document: {
                        val: null,
                        isValid: true,
                        msg: "",
                    }
                }
                this.inputAttachments.push(obj2);
            }
        },
        onPickFile(ref) {
            this.$refs[ref].click();
        },
        handleUploadedPhoto(event) {
            this.logoPicture.isValid = true;
            this.logoPicture.msg = "";
            const file = event.target.files[0];
            if (file) {
                if (!file.type.includes('image/')) {
                    this.logoPicture.isValid = false;
                    this.logoPicture.msg = 'Only JPG, JPEG and PNG files are allowed.';
                } else if (file.size > 2 * 1024 * 1024) {
                    this.logoPicture.isValid = false;
                    this.logoPicture.msg = 'File size must be less than 2MB.';
                } else {
                    this.logoPicture.isValid = true;
                    this.logoPicture.msg = '';
                    this.logoPicture.val = file;
                    this.logoPreview = URL.createObjectURL(file);
                }
            }
        },
        cancelUploadedPhoto() {
            this.logoPicture.val = null;
            this.logoPreview = null;
        },
        handleFileChange(event) {
            const fileIndex = parseInt(event.target.id.split('-').pop());
            const targetIndex = isNaN(fileIndex) ? this.attachmentIndex : fileIndex;

            this.inputAttachments[targetIndex].document.isValid = true;
            this.inputAttachments[targetIndex].document.msg = "";
            const file = event.target.files[0];

            if (file) {
                if (!this.isValidFileType(file)) {
                    this.inputAttachments[targetIndex].document.isValid = false;
                    this.inputAttachments[targetIndex].document.msg = 'Only JPG, JPEG, PNG, PDF, DOC, DOCX, PPT, and PPTX files are allowed.';
                } else if (file.size > 16 * 1024 * 1024) {
                    this.inputAttachments[targetIndex].document.isValid = false;
                    this.inputAttachments[targetIndex].document.msg = 'File size must be less than 16MB.';
                } else {
                    this.inputAttachments[targetIndex].document.isValid = true;
                    this.inputAttachments[targetIndex].document.msg = '';
                    this.inputAttachments[targetIndex].document.val = file;
                    this.inputAttachments[targetIndex].document.is_new = true;
                    this.inputAttachments[targetIndex].document.id = null;
                    this.inputAttachments[targetIndex].document.original_filename = "";
                    this.inputAttachments[targetIndex].document.path = "";
                }
            }

        },
        handleManagementApprovalFile(event, option) {
            const indexKey = `${option}AttachmentIndex`;
            const index = this[indexKey];

            if (!this[option] || !this[option].attachments || !this[option].attachments[index]) {
                console.error("Invalid state for attachments");
                return;
            }

            this[option].attachments[index].document.isValid = true;
            this[option].attachments[index].document.msg = "";

            const file = event.target.files[0];

            if (file) {
                if (!this.isValidFileType(file)) {
                    this[option].attachments[index].document.isValid = false;
                    this[option].attachments[index].document.msg = 'Only JPG, JPEG, PNG, PDF, DOC, DOCX, PPT, and PPTX files are allowed.';
                } else {
                    this[option].attachments[index].document.isValid = true;
                    this[option].attachments[index].document.msg = '';
                    this[option].attachments[index].document.val = file;
                }
            }
        },
    },
}
</script>

<style lang="scss" scoped>
.form-container {
    @apply h-full w-full flex flex-col p-6 md:p-8;
    max-width: 56rem;
    margin: 0 auto;
}

.form-section {
    @apply mb-10;
    padding: 1.5rem 1.75rem;
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    box-shadow: 0 1px 3px 0 rgb(0 0 0 / 0.05);
}

.section-header {
    @apply flex items-center gap-3 mb-6 pb-4;
    border-bottom: 1px solid #f1f5f9;
}

.section-icon {
    @apply flex items-center justify-center w-10 h-10 rounded-lg;
    background: linear-gradient(135deg, #212a3e 0%, #394867 100%);
    color: #fff;
}

.section-icon-approval {
    background: linear-gradient(135deg, #059669 0%, #047857 100%);
}

.section-title {
    @apply text-lg font-semibold text-slate-800;
    letter-spacing: -0.01em;
}

.logo-upload-card {
    @apply flex flex-col items-start gap-4 mb-6;
}

.logo-preview-wrapper {
    @apply relative inline-block;
}

.logo-remove-btn {
    @apply absolute -top-2 -right-2 w-7 h-7 flex items-center justify-center rounded-full cursor-pointer;
    background: #fef2f2;
    border: 1px solid #fecaca;
    color: #dc2626;
}

.logo-preview-img {
    @apply h-36 w-36 object-contain rounded-xl;
    border: 1px solid #e2e8f0;
}

.logo-placeholder {
    @apply flex flex-col items-center justify-center w-36 h-36 rounded-xl;
    background: #f8fafc;
    border: 2px dashed #cbd5e1;
}

.logo-upload-btn {
    @apply flex flex-col items-center gap-1 px-4 py-3 rounded-lg cursor-pointer transition-all duration-200;
    background: #f1f5f9;
    border: 1px solid #e2e8f0;
    color: #475569;
}

.logo-upload-btn:hover {
    background: #e2e8f0;
    color: #212a3e;
    border-color: #cbd5e1;
}

.logo-upload-btn-error {
    border-color: #fecaca;
    color: #dc2626;
    background: #fef2f2;
}

.logo-upload-hint {
    @apply text-xs text-slate-500;
}

.form-grid {
    @apply grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5;
}

.form-group {
    @apply flex flex-col gap-1.5;
}

.form-group-error .form-label {
    color: #dc2626;
}

.form-group-error .form-input,
.form-group-error .form-select,
.form-group-error .form-textarea {
    border-color: #fca5a5;
    box-shadow: 0 0 0 1px #fecaca;
}

.form-label {
    @apply text-sm font-medium text-slate-700;
}

.required {
    color: #dc2626;
}

.form-input,
.form-select,
.form-textarea {
    @apply block w-full rounded-lg py-2.5 px-3 text-sm;
    border: 1px solid #e2e8f0;
    background: #fff;
}

.form-textarea {
    resize: vertical;
    min-height: 5rem;
}

.form-input:focus,
.form-select:focus,
.form-textarea:focus {
    outline: none;
    border-color: #212a3e;
    box-shadow: 0 0 0 3px rgba(33, 42, 62, 0.1);
}

.form-input-disabled {
    background: #f8fafc;
    color: #64748b;
}

.form-input-error {
    border-color: #fca5a5;
    box-shadow: 0 0 0 1px #fecaca;
}

.form-error-msg {
    @apply text-xs text-rose-600 mt-0.5;
}

.form-error-msg-center {
    @apply text-center mt-2;
}

.entity-group {
    @apply mt-5;
}

.entity-checkbox-grid {
    @apply flex flex-wrap gap-2;
}

.entity-checkbox-item {
    @apply flex items-center gap-2 px-4 py-2.5 rounded-lg cursor-pointer transition-all duration-200;
    border: 1px solid #e2e8f0;
    background: #f8fafc;
}

.entity-checkbox-item:hover {
    border-color: #cbd5e1;
    background: #f1f5f9;
}

.entity-checkbox-item-selected {
    border-color: #212a3e;
    background: #212a3e;
    color: #fff;
}

.entity-checkbox-input {
    @apply sr-only;
}

.entity-checkbox-label {
    @apply text-sm font-medium;
}

.approval-grid {
    @apply space-y-5;
}

.approval-item {
    @apply p-4 rounded-lg;
    background: #f8fafc;
    border: 1px solid #f1f5f9;
}

.approval-row {
    @apply flex flex-wrap items-center gap-4;
}

.approval-radio {
    @apply flex items-center gap-2 cursor-pointer;
}

.approval-file-wrapper {
    @apply ml-auto;
}

.approval-file-input {
    display: block;
    font-size: 0.875rem;
    color: #475569;
}

.approval-file-input::-webkit-file-upload-button,
.approval-file-input::file-selector-button {
    margin-right: 1rem;
    padding: 0.5rem 1rem;
    border-radius: 0.5rem;
    border: 0;
    font-size: 0.875rem;
    font-weight: 500;
    background: #f1f5f9;
    color: #334155;
    cursor: pointer;
}

.contact-person-row,
.document-row {
    @apply flex items-start gap-4 p-4 rounded-lg mb-4;
    background: #f8fafc;
    border: 1px solid #f1f5f9;
}

.contact-person-badge,
.document-badge {
    @apply flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-lg text-sm font-semibold;
    background: #212a3e;
    color: #fff;
}

.contact-person-fields,
.document-fields {
    @apply flex-grow grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4;
}

.document-fields {
    @apply lg:grid-cols-2;
}

.contact-person-actions,
.document-actions {
    @apply flex flex-col gap-2 flex-shrink-0;
}

.action-btn {
    @apply flex items-center justify-center w-9 h-9 rounded-lg cursor-pointer transition-all duration-200;
}

.action-btn-add {
    background: #212a3e;
    color: #fff;
    border: 1px solid #212a3e;
}

.action-btn-add:hover {
    background: #394867;
    border-color: #394867;
}

.action-btn-remove {
    background: #fff;
    color: #dc2626;
    border: 1px solid #fecaca;
}

.action-btn-remove:hover {
    background: #fef2f2;
}

.document-existing {
    @apply flex items-center gap-2 p-3 rounded-lg;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
}

.document-link {
    @apply text-sm text-twelve hover:underline truncate flex-1 min-w-0;
}

.document-remove-btn {
    @apply flex items-center justify-center w-8 h-8 rounded cursor-pointer text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors;
}

.file-input-wrapper {
    @apply flex flex-col gap-1;
}

.file-input {
    display: block;
    width: 100%;
    font-size: 0.875rem;
    color: #475569;
}

.file-input::-webkit-file-upload-button,
.file-input::file-selector-button {
    margin-right: 1rem;
    padding: 0.5rem 1rem;
    border-radius: 0.5rem;
    border: 0;
    font-size: 0.875rem;
    font-weight: 500;
    background: #f1f5f9;
    color: #334155;
    cursor: pointer;
}

.file-input-hint {
    @apply text-xs text-slate-500;
}

.form-footer {
    @apply flex justify-center pt-8 pb-4;
}

.submit-loading {
    @apply px-8 py-3 rounded-xl border-2 border-ternary;
}

.submit-btn {
    @apply px-10 py-3 rounded-xl font-semibold text-sm uppercase tracking-wide transition-all duration-200;
    background: #212a3e;
    color: #fff;
    border: 2px solid #212a3e;
}

.submit-btn:hover {
    background: #394867;
    border-color: #394867;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(33, 42, 62, 0.25);
}

.fade-enter-active,
.fade-leave-active {
    transition: all 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
    height: 0;
}

.form-fade-enter-active,
.form-fade-leave-active {
    transition: all 0.3s ease;
}

.form-fade-enter-from,
.form-fade-leave-to {
    opacity: 0;
    height: 0;
}

.form-group-multiselect :deep(.multiselect__tags) {
    min-height: 2.625rem;
    border-radius: 0.5rem;
    border: 1px solid #e2e8f0;
    padding-top: 0.35rem;
}
.form-group-multiselect :deep(.multiselect__input),
.form-group-multiselect :deep(.multiselect__single) {
    font-size: 0.875rem;
}
.form-group-multiselect :deep(.multiselect--active .multiselect__tags) {
    border-color: #212a3e;
    box-shadow: 0 0 0 3px rgba(33, 42, 62, 0.1);
}

.option__desc {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.option__title {
    font-weight: 500;
}

.option__small {
    font-size: 0.75rem;
    color: #64748b;
}
</style>
